import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PlayLessonRoundedIcon from '@mui/icons-material/PlayLessonRounded';
import {
  Box,
  Container,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { SectionTitleFontSize } from '../HomeView';

interface CourseCardProps {
  accentColor?: (theme: any) => string | string;
  title: string;
  description: string;
  modules: string[];
  outcomes: string[];
}

const CourseCard = ({
  accentColor,
  title,
  description,
  modules,
  outcomes,
}: CourseCardProps) => {
  const theme = useTheme();

  return (
    <Box
      component="section"
      aria-labelledby={title.replace(/\s+/g, '-').toLowerCase()}
      sx={{
        p: { xs: 3, sm: 4 },
        borderRadius: 2,
        boxShadow: 3,
        bgcolor:
          theme.palette.mode == 'dark'
            ? '#28343f'
            : theme.palette.background.paper,
        borderLeft: 6,
        borderColor: accentColor
          ? typeof accentColor === 'function'
            ? accentColor(theme)
            : accentColor
          : theme.palette.primary.main,
        height: '100%',
      }}
    >
      <Typography
        id={title.replace(/\s+/g, '-').toLowerCase()}
        variant="h5"
        fontWeight={700}
        gutterBottom
      >
        {title}
      </Typography>

      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        {description}
      </Typography>

      <Box sx={{ display: 'grid', gap: 3 }}>
        {/* Modules */}
        <Box component="div">
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Modules
          </Typography>
          <List dense sx={{ py: 0 }}>
            {modules.map((m, idx) => (
              <ListItem
                key={idx}
                disableGutters
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <ListItemIcon sx={{ minWidth: 34 }}>
                  <PlayLessonRoundedIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ variant: 'body1' }}
                  primary={m}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Learners will */}
        <Box component="div">
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Learners will
          </Typography>
          <List dense sx={{ py: 0 }}>
            {outcomes.map((o, idx) => (
              <ListItem
                key={idx}
                disableGutters
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <ListItemIcon sx={{ minWidth: 34 }}>
                  <CheckCircleRoundedIcon fontSize="small" color="success" />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ variant: 'body1' }}
                  primary={o}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>
    </Box>
  );
};

const WhatsIncludedSection = () => {
  return (
    <Box sx={{ py: 8 }}>
      <Container maxWidth="lg">
        <Typography
          variant="h4"
          fontWeight={700}
          align="center"
          gutterBottom
          fontSize={SectionTitleFontSize}
        >
          What&apos;s Included in Your Subscription
        </Typography>

        <Grid container spacing={4} sx={{ mt: 1 }}>
          <Grid item sm={12} md={4}>
            <CourseCard
              accentColor={(t) => t.palette.primary.main}
              title="Active Shooter Response Training"
              description={
                'This course equips learners to recognize warning signs, overcome fear responses, and take action in violent situations using the Run, Hide, Fight model.'
              }
              modules={[
                'Active Shootings and Workplace Violence',
                'The Situational Awareness Model',
                'The Effects of Life Threatening Encounters',
                'Run, Hide, Fight Strategy',
              ]}
              outcomes={[
                'Improve their situational awareness',
                'Understand the psychology of fear under threat',
                'Respond quickly and decisively in crisis',
                'Apply survival strategies to protect themselves and others',
              ]}
            />
          </Grid>
          <Grid item sm={12} md={4}>
            <CourseCard
              accentColor={(t) => t.palette.primary.main}
              title="The Art of De-Escalation"
              description="De-escalation isn't just about words - it's about tone, timing, presence, and empathy. 
                 This course teaches students how to stay composed, identify verbal and non-verbal risk factors, 
                 and apply proven communication techniques."
              modules={[
                'Conflict & Emotions',
                'Staying Calm & Using Effective Words',
                'Your Voice & Use of Body Language',
                'Applying Active Listening Skills',
              ]}
              outcomes={[
                'Understand how conflict affects the brain and body',
                'Respond rather than react in emotionally charged moments',
                'Use calming body language and tone',
                'Apply proven de-escalation models such as emotion labeling and mirroring',
              ]}
            />
          </Grid>
          <Grid item sm={12} md={4}>
            <CourseCard
              accentColor={() => '#6C63FF'}
              title="Mastering Emotional Intelligence & Conflict Resilience"
              description="Emotional intelligence is the foundation for staying calm, self-aware, and effective during conflict. 
     This course teaches students how to recognize emotional triggers, manage stress responses, 
     and build resilience in difficult interpersonal situations."
              modules={[
                'Introduction to Emotional Intelligence & Conflict Resilience',
                'The Four Domains of Emotional Intelligence',
                'Building Conflict Resilience',
              ]}
              outcomes={[
                'Define emotional intelligence and conflict resilience',
                'Recognize emotional triggers and personal responses',
                'Use self-management techniques in stressful situations',
                'Apply empathy, active listening, and solution-focused communication during conflict',
              ]}
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default WhatsIncludedSection;
